import { useState, type CSSProperties } from 'react';
import type { EventInstanceRecord, OrganizationBrandingContent } from '../api/types';
import vcodeDefaultLogo from '../../assets/branding/vcode-default-mark.svg';

export const STUDIO_PALETTES = {
  violet: { accent: '#4935ee', strong: '#3424b7', ink: '#292039', highlight: '#dcfa64' },
  ocean: { accent: '#155bb8', strong: '#104485', ink: '#142c45', highlight: '#dcfa64' },
  forest: { accent: '#176b53', strong: '#11513f', ink: '#18392e', highlight: '#dcfa64' },
  terracotta: { accent: '#9f3528', strong: '#7c281f', ink: '#20372e', highlight: '#dcfa64' },
} as const;

/** One approved palette for templates, working surfaces and consumer-owned portals. */
export function studioPaletteStyle(paletteId: OrganizationBrandingContent['paletteId']): CSSProperties {
  const palette = STUDIO_PALETTES[paletteId];
  const red = Number.parseInt(palette.accent.slice(1, 3), 16);
  const green = Number.parseInt(palette.accent.slice(3, 5), 16);
  const blue = Number.parseInt(palette.accent.slice(5, 7), 16);
  const soft = `rgb(${Math.round(red * .08 + 255 * .92)} ${Math.round(green * .08 + 255 * .92)} ${Math.round(blue * .08 + 255 * .92)})`;
  return {
    '--studio-accent': palette.accent, '--studio-accent-strong': palette.strong,
    '--studio-ink': palette.ink, '--studio-highlight': palette.highlight,
    '--brand-accent': palette.accent,
    '--brand-accent-strong': palette.strong,
    '--brand-accent-soft': soft,
    '--brand-glow': `rgba(${red}, ${green}, ${blue}, 0.18)`,
    '--brand-canvas': `rgba(${red}, ${green}, ${blue}, 0.08)`,
    '--brand-border': `rgba(${red}, ${green}, ${blue}, 0.24)`,
    '--stratos-color-primary': palette.accent,
    '--stratos-color-primary-soft': soft,
    '--stratos-color-on-primary': '#f9fffd',
    '--stratos-color-accent': palette.accent,
    '--stratos-color-accent-strong': palette.strong,
    '--stratos-color-accent-soft': soft,
    '--stratos-color-focus-ring': palette.accent,
  } as CSSProperties;
}

/** Tenant-owned type and surface tokens, also copied into consumer-owned dialogs. */
export function organizationWorkspaceStyle(branding: OrganizationBrandingContent): CSSProperties {
  const palette = studioPaletteStyle(branding.paletteId);
  if (branding.templateId === 'standard') return palette;
  const editorial = branding.templateId === 'editorial';
  const canvas = editorial ? '#fbf9f3' : branding.templateId === 'studio' ? '#f5f3fb' : '#fafafe';
  const sans = "EventManrope, 'Avenir Next', 'Segoe UI', sans-serif";
  return { ...palette,
    '--workspace-font-sans': sans,
    '--workspace-font-heading': editorial ? 'EventDMSerif, Georgia, serif' : sans,
    '--workspace-canvas': canvas,
    '--workspace-surface': editorial ? '#fdfbf7' : '#ffffff',
    '--color-text': STUDIO_PALETTES[branding.paletteId].ink,
    '--color-text-muted': '#605c70',
    '--color-surface': canvas,
    '--color-surface-strong': editorial ? '#fdfbf7' : '#ffffff',
    '--color-surface-alt': palette['--brand-accent-soft' as keyof CSSProperties],
    '--color-border': editorial ? '#dedbd2' : '#e1deed',
    '--color-border-strong': editorial ? '#a5a198' : '#a6a1b5',
    '--stratos-font-sans': sans,
    '--stratos-color-text': STUDIO_PALETTES[branding.paletteId].ink,
    '--stratos-color-muted': '#605c70',
    '--stratos-color-text-secondary': '#605c70',
    '--stratos-color-surface': editorial ? '#fdfbf7' : '#ffffff',
    '--stratos-color-border-strong': editorial ? '#a5a198' : '#a6a1b5',
    fontFamily: sans,
  } as CSSProperties;
}

export interface StudioEventsViewProps {
  readonly branding: OrganizationBrandingContent;
  readonly events: ReadonlyArray<EventInstanceRecord>;
  readonly locale: 'cs' | 'en';
  readonly preview?: boolean;
  readonly logoUrl?: string;
  readonly heroUrl?: string;
  readonly includeHeader?: boolean;
}

export function BrandingWordmark({ branding, logoUrl, showNameWithLogo = false }: Pick<StudioEventsViewProps, 'branding' | 'logoUrl'> & {
  readonly showNameWithLogo?: boolean;
}) {
  const [failedUrl, setFailedUrl] = useState<string>();
  const defaultLogo = branding.logoAssetId === null;
  const source = defaultLogo ? vcodeDefaultLogo : logoUrl;
  const showLogo = Boolean(source && source !== failedUrl);
  return <>{showLogo && <img src={source} alt={defaultLogo ? 'VCode' : branding.logoAlt} onError={() => setFailedUrl(source)} />}
    {(!showLogo || defaultLogo || showNameWithLogo) && <strong>{branding.displayName}</strong>}<span>Event</span></>;
}

export function StandardBrandingWordmark(props: Pick<StudioEventsViewProps, 'branding' | 'logoUrl'>) {
  return <span className="standard-wordmark"><BrandingWordmark {...props} showNameWithLogo /></span>;
}

/** Presentation default only, never an implicit saved organization configuration. */
export function DefaultOrganizationWordmark({ organizationName }: { readonly organizationName: string }) {
  const [failed, setFailed] = useState(false);
  return <span className="standard-wordmark">{!failed && <img src={vcodeDefaultLogo} alt="VCode" onError={() => setFailed(true)} />}
    <strong>{organizationName}</strong><span>Event</span></span>;
}
